import type {CreateMaterialOrder, MaterialOrder} from '@domain/material-storage'
import {ApiClient, type ErrorResponse} from '@hconnect/apiclient'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, type UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {notifyIfErrorMessage} from '../utils'

type Variables = {
  plantCode: string
  dto: CreateMaterialOrder
}

export const useAddMaterialOrder = (
  options?: Omit<
    UseMutationOptions<MaterialOrder, AxiosError<ErrorResponse>, Variables>,
    'mutationFn'
  >
) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const raiseError = useErrorHandler()
  const queryClient = useQueryClient()
  return useMutation<MaterialOrder, AxiosError<ErrorResponse>, Variables>({
    ...options,
    mutationKey: ['addMaterialOrder'],
    mutationFn: async ({plantCode, dto}) => {
      const response = await ApiClient.axiosInstance.post<MaterialOrder>(
        `/plants/${plantCode}/material-orders`,
        dto
      )
      return response.data
    },
    onError: (error) => {
      notifyIfErrorMessage(error.response?.data.detail, raiseError, spawnErrorToast)
    },
    onSuccess: () => {
      spawnSuccessToast(t('success.addMaterialOrder'))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: ['materialOrders'], refetchType: 'all'})
      void queryClient.invalidateQueries({queryKey: ['stockDevelopment'], refetchType: 'all'})
    }
  })
}
